import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Camera, Car, Loader2, MapPin, Navigation, Pencil, Receipt, ScanLine, Upload, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha, hoyISO } from "@/lib/format";
import { useEmpresa } from "@/hooks/useEmpresa";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import GastoImagen from "@/components/GastoImagen";
import GastoIvasEditor, { ivasFromApi, ivasToJson } from "@/components/GastoIvasEditor";
import KilometrajeForm from "@/components/KilometrajeForm";

const ESTADO = {
  pendiente: { label: "Pendiente", cls: "bg-amber-100 text-amber-800" },
  aprobado: { label: "Aprobado", cls: "bg-emerald-100 text-emerald-800" },
  rechazado: { label: "Rechazado", cls: "bg-rose-100 text-rose-800" },
};

const LIQUIDADO = { label: "Liquidado", cls: "bg-sky-100 text-sky-800" };

const FORM_VACIO = {
  categoria: "ticket",
  proyecto_id: "",
  cliente_id: "",
  fecha_gasto: hoyISO(),
  proveedor: "",
  proveedor_nif: "",
  proveedor_direccion: "",
  proveedor_poblacion: "",
  proveedor_provincia: "",
  proveedor_codigo_postal: "",
  proveedor_telefono: "",
  numero_documento: "",
  importe: "",
  moneda: "EUR",
  notas: "",
  motivo_visita: "",
  origen_direccion: "",
  destino_direccion: "",
  km: "",
  tarifa_km: "",
  visitas: [],
  ruta_comercial_id: "",
  km_ruta_estimada: "",
  recorrido: null,
  mantener_orden_visitas: true,
  ivas: [],
  iva_incluido_sin_desglose: false,
  ivas_calculados: false,
  ruta_parada_id: null,
  visita_label: "",
};

function importeParaInput(val) {
  if (val == null || val === "") return "";
  return String(val).replace(".", ",");
}

function esDesplazamiento(cat) {
  return cat === "kilometraje";
}

function requiereTicket(cat) {
  return cat !== "kilometraje";
}

export default function MisGastos() {
  const { empresa, cargando: cargandoEmpresa } = useEmpresa();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [tarifaKmDefault, setTarifaKmDefault] = useState(0.26);
  const [cargando, setCargando] = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const [escaneando, setEscaneando] = useState(false);
  const [calculandoRuta, setCalculandoRuta] = useState(false);
  const [escaneado, setEscaneado] = useState(false);
  const [preview, setPreview] = useState(null);
  const [archivo, setArchivo] = useState(null);
  const [archivoFin, setArchivoFin] = useState(null);
  const [previewFin, setPreviewFin] = useState(null);
  const [rutasHoy, setRutasHoy] = useState([]);
  const [form, setForm] = useState(FORM_VACIO);
  const [gps, setGps] = useState({ latitud: null, longitud: null });
  const [favoritas, setFavoritas] = useState([]);
  const [liquidaciones, setLiquidaciones] = useState([]);
  const [scanMeta, setScanMeta] = useState(null);
  const [mostrarTextoOcr, setMostrarTextoOcr] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const inputRef = useRef(null);

  const usaProyectos = !!empresa?.gestionar_proyectos;
  const usaRutas = !!empresa?.gestionar_rutas_comerciales;
  const esKm = esDesplazamiento(form.categoria);
  const necesitaTicket = requiereTicket(form.categoria);

  const formListo = useMemo(() => {
    if (esKm) return Boolean(form.km);
    return escaneado || Boolean(archivo) || Boolean(editandoId);
  }, [esKm, form.km, escaneado, archivo, editandoId]);

  const cargar = useCallback(async () => {
    try {
      const peticiones = [
        api.get("/gastos/mis"),
        api.get("/gastos/categorias"),
        api.get("/gastos/liquidaciones/mis"),
      ];
      if (usaProyectos) {
        peticiones.push(api.get("/proyectos/mis/asignaciones", { params: { para_gastos: true } }));
      } else {
        peticiones.push(Promise.resolve({ data: { items: [] } }));
      }
      if (usaProyectos || usaRutas) {
        peticiones.push(
          api.get(usaRutas ? "/rutas-comerciales/clientes" : "/proyectos/clientes", { params: { activos: true } })
            .catch(() => ({ data: { items: [] } }))
        );
      } else {
        peticiones.push(Promise.resolve({ data: { items: [] } }));
      }
      if (usaRutas) {
        peticiones.push(api.get("/rutas-comerciales/hoy").catch(() => ({ data: { ruta: null } })));
      } else {
        peticiones.push(Promise.resolve({ data: { ruta: null } }));
      }
      const [g, cats, liqs, p, c, rh] = await Promise.all(peticiones);
      setItems(g.data.items || []);
      setCategorias(cats.data.items || []);
      if (cats.data.tarifa_km != null) setTarifaKmDefault(cats.data.tarifa_km);
      setLiquidaciones(liqs.data.items || []);
      setProyectos(p ? (p.data.items || []) : []);
      setClientes(c ? (c.data.items || []).filter((x) => x.activo !== false) : []);
      const rutaHoy = rh?.data?.ruta;
      setRutasHoy(rutaHoy ? [rutaHoy] : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargando(false);
    }
  }, [usaProyectos, usaRutas]);

  useEffect(() => {
    if (!cargandoEmpresa) cargar();
  }, [cargandoEmpresa, cargar]);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setGps({ latitud: pos.coords.latitude, longitud: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/gastos/sugerencias-visita");
        setFavoritas(data.direcciones_favoritas || []);
        setForm((prev) => ({
          ...prev,
          proyecto_id: prev.proyecto_id || (data.proyecto_id ? String(data.proyecto_id) : ""),
          cliente_id: prev.cliente_id || (data.cliente_id ? String(data.cliente_id) : ""),
          tarifa_km: prev.tarifa_km || String(tarifaKmDefault).replace(".", ","),
        }));
        if (data.latitud != null && data.longitud != null) {
          setGps({ latitud: data.latitud, longitud: data.longitud });
        }
      } catch {
        /* opcional */
      }
    })();
  }, [tarifaKmDefault]);

  useEffect(() => {
    if (cargando) return;
    const raw = sessionStorage.getItem("gasto_ruta_parada");
    if (!raw) return;
    try {
      const ctx = JSON.parse(raw);
      sessionStorage.removeItem("gasto_ruta_parada");
      const visita = (ctx.nombre || ctx.direccion || ctx.cliente_id)
        ? [{
            tipo: ctx.cliente_id ? "cliente" : "mapa",
            cliente_id: ctx.cliente_id || null,
            nombre: ctx.nombre || "",
            direccion: ctx.direccion || "",
            lat: ctx.latitud ?? null,
            lng: ctx.longitud ?? null,
          }]
        : [];
      setForm((prev) => ({
        ...prev,
        categoria: ctx.categoria || prev.categoria,
        cliente_id: ctx.categoria === "kilometraje" ? "" : (ctx.cliente_id ? String(ctx.cliente_id) : prev.cliente_id),
        proyecto_id: ctx.proyecto_id ? String(ctx.proyecto_id) : (prev.proyecto_id && prev.proyecto_id !== "none" ? prev.proyecto_id : (ctx.ruta_parada_id && ctx.categoria !== "kilometraje" ? "ruta" : prev.proyecto_id)),
        motivo_visita: ctx.motivo_visita || (ctx.nombre ? `Visita comercial · ${ctx.nombre}` : prev.motivo_visita),
        importe: ctx.importe ? String(ctx.importe).replace(".", ",") : prev.importe,
        destino_direccion: ctx.categoria === "kilometraje" ? prev.destino_direccion : (ctx.direccion || prev.destino_direccion),
        ruta_parada_id: ctx.categoria === "kilometraje" ? null : (ctx.ruta_parada_id || null),
        visita_label: ctx.nombre ? `Visita · ${ctx.nombre}` : "Visita comercial",
        visitas: ctx.categoria === "kilometraje" ? [...(prev.visitas || []), ...visita] : prev.visitas,
        ruta_comercial_id: ctx.ruta_id ? String(ctx.ruta_id) : prev.ruta_comercial_id,
      }));
      if (ctx.cliente_id && ctx.nombre) {
        setClientes((prev) => (
          prev.some((c) => String(c.id) === String(ctx.cliente_id))
            ? prev
            : [{ id: ctx.cliente_id, nombre: ctx.nombre, activo: true, direccion: ctx.direccion, latitud: ctx.latitud, longitud: ctx.longitud }, ...prev]
        ));
      }
      if (ctx.latitud != null && ctx.longitud != null) {
        setGps({ latitud: ctx.latitud, longitud: ctx.longitud });
      }
      toast.message(ctx.categoria === "kilometraje"
        ? "Visita añadida a la memoria de kilometraje."
        : "Datos de la visita rellenados. Solo falta la foto del ticket.");
    } catch {
      /* ignore */
    }
  }, [cargando]);

  const resetNuevo = () => {
    setArchivo(null);
    setArchivoFin(null);
    setPreview(null);
    setPreviewFin(null);
    setEscaneado(false);
    setEditandoId(null);
    setForm({ ...FORM_VACIO, tarifa_km: String(tarifaKmDefault).replace(".", ","), fecha_gasto: hoyISO() });
    setScanMeta(null);
    setMostrarTextoOcr(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const setCategoria = (id) => {
    setForm((prev) => ({
      ...prev,
      categoria: id,
      tarifa_km: prev.tarifa_km || String(tarifaKmDefault).replace(".", ","),
    }));
    if (id === "kilometraje") {
      setEscaneado(true);
    } else if (!archivo && !editandoId) {
      setEscaneado(false);
    }
  };

  const escanearArchivo = async (file) => {
    setEscaneando(true);
    const fd = new FormData();
    fd.append("imagen", file);
    try {
      const { data } = await api.post("/gastos/escanear", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({
        ...prev,
        fecha_gasto: data.fecha || prev.fecha_gasto || hoyISO(),
        proveedor: data.proveedor || "",
        proveedor_nif: data.proveedor_nif || "",
        proveedor_direccion: data.proveedor_direccion || "",
        proveedor_poblacion: data.proveedor_poblacion || "",
        proveedor_provincia: data.proveedor_provincia || "",
        proveedor_codigo_postal: data.proveedor_codigo_postal || "",
        proveedor_telefono: data.proveedor_telefono || "",
        numero_documento: data.numero_documento || "",
        importe: importeParaInput(data.importe),
        moneda: data.moneda || "EUR",
        ivas: ivasFromApi(data.ivas),
        iva_incluido_sin_desglose: Boolean(data.iva_incluido_sin_desglose),
        ivas_calculados: Boolean(data.ivas_calculados),
      }));
      setScanMeta({
        ocr_disponible: Boolean(data.ocr_disponible),
        extraccion_fuente: data.extraccion_fuente || "",
        ubicacion_corregida: Boolean(data.ubicacion_corregida),
        conocimiento_aplicado: Boolean(data.conocimiento_aplicado),
        texto: data.texto || "",
      });
      setMostrarTextoOcr(false);
      setEscaneado(true);
      toast.success(data.ocr_disponible ? "Ticket escaneado. Revisa los datos." : "Complétalo manualmente.");
    } catch (e) {
      toast.error(formatApiError(e));
      resetNuevo();
    } finally {
      setEscaneando(false);
    }
  };

  const onArchivo = async (file) => {
    if (!file) return;
    const ok =
      file.type.startsWith("image/") ||
      file.type === "application/pdf" ||
      file.name?.toLowerCase().endsWith(".pdf");
    if (!ok) {
      toast.error("Selecciona una imagen (JPG, PNG, WEBP) o un PDF");
      return;
    }
    setArchivo(file);
    if (file.type.startsWith("image/")) setPreview(URL.createObjectURL(file));
    else setPreview(null);
    await escanearArchivo(file);
  };

  const calcularRuta = async () => {
    if (!form.origen_direccion || !form.destino_direccion) {
      toast.error("Indica origen y destino");
      return;
    }
    setCalculandoRuta(true);
    try {
      const { data } = await api.post("/gastos/calcular-ruta", {
        origen: form.origen_direccion,
        destino: form.destino_direccion,
        tarifa_km: form.tarifa_km ? Number(String(form.tarifa_km).replace(",", ".")) : undefined,
      });
      setForm((prev) => ({
        ...prev,
        origen_direccion: data.origen?.direccion || prev.origen_direccion,
        destino_direccion: data.destino?.direccion || prev.destino_direccion,
        km: importeParaInput(data.km),
        importe: importeParaInput(data.importe),
        tarifa_km: importeParaInput(data.tarifa_km),
        moneda: "EUR",
        proveedor: prev.proveedor || "Kilometraje",
      }));
      toast.success(`Ruta: ${data.km} km · ${Number(data.importe).toFixed(2)} €`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCalculandoRuta(false);
    }
  };

  const guardarFavorita = async (direccion, etiqueta = "") => {
    const d = (direccion || "").trim();
    if (d.length < 3) {
      toast.error("Escribe una dirección primero");
      return;
    }
    try {
      await api.post("/gastos/direcciones-favoritas", { direccion: d, etiqueta });
      const { data } = await api.get("/gastos/sugerencias-visita");
      setFavoritas(data.direcciones_favoritas || []);
      toast.success("Dirección guardada en favoritos");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const appendFormData = (fd, forzar = false) => {
    fd.append("categoria", form.categoria);
    if (archivo) fd.append("imagen", archivo);
    if (esKm && archivoFin) fd.append("foto_odometro_fin", archivoFin);
    if (form.proyecto_id && form.proyecto_id !== "ruta") fd.append("proyecto_id", form.proyecto_id);
    if (!esKm && form.cliente_id) fd.append("cliente_id", form.cliente_id);
    if (!esKm && form.ruta_parada_id) fd.append("ruta_parada_id", String(form.ruta_parada_id));
    if (esKm && form.ruta_comercial_id) fd.append("ruta_comercial_id", String(form.ruta_comercial_id));
    if (esKm) fd.append("visitas_json", JSON.stringify(form.visitas || []));
    if (esKm) fd.append("mantener_orden_visitas", form.mantener_orden_visitas !== false ? "true" : "false");
    if (esKm && form.km_ruta_estimada) fd.append("km_ruta_estimada", String(form.km_ruta_estimada).replace(",", "."));
    if (esKm && form.recorrido) fd.append("recorrido_json", JSON.stringify(form.recorrido));
    if (form.fecha_gasto) fd.append("fecha_gasto", form.fecha_gasto);
    if (form.proveedor) fd.append("proveedor", form.proveedor);
    if (form.proveedor_nif) fd.append("proveedor_nif", form.proveedor_nif);
    if (form.proveedor_direccion) fd.append("proveedor_direccion", form.proveedor_direccion);
    if (form.proveedor_poblacion) fd.append("proveedor_poblacion", form.proveedor_poblacion);
    if (form.proveedor_provincia) fd.append("proveedor_provincia", form.proveedor_provincia);
    if (form.proveedor_codigo_postal) fd.append("proveedor_codigo_postal", form.proveedor_codigo_postal);
    if (form.proveedor_telefono) fd.append("proveedor_telefono", form.proveedor_telefono);
    if (form.numero_documento) fd.append("numero_documento", form.numero_documento);
    if (form.importe) fd.append("importe", form.importe);
    if (form.moneda) fd.append("moneda", form.moneda);
    const ivasJson = ivasToJson(form.ivas);
    if (ivasJson) fd.append("ivas_json", ivasJson);
    if (form.notas) fd.append("notas", form.notas);
    if (form.motivo_visita) fd.append("motivo_visita", form.motivo_visita);
    if (form.origen_direccion) fd.append("origen_direccion", form.origen_direccion);
    if (form.destino_direccion) fd.append("destino_direccion", form.destino_direccion);
    if (form.km) fd.append("km", form.km);
    if (form.tarifa_km) fd.append("tarifa_km", form.tarifa_km);
    if (gps.latitud != null) fd.append("latitud", String(gps.latitud));
    if (gps.longitud != null) fd.append("longitud", String(gps.longitud));
    if (forzar) fd.append("forzar_duplicado", "true");
  };

  const editarPendiente = (g) => {
    if (g.estado !== "pendiente") {
      toast.error("Solo se pueden modificar gastos pendientes");
      return;
    }
    setEditandoId(g.id);
    setArchivo(null);
    setPreview(null);
    setEscaneado(true);
    setScanMeta(null);
    setMostrarTextoOcr(false);
    if (inputRef.current) inputRef.current.value = "";
    setForm({
      ...FORM_VACIO,
      categoria: g.categoria || "ticket",
      proyecto_id: g.proyecto_id ? String(g.proyecto_id) : (g.ruta_parada_id ? "ruta" : ""),
      cliente_id: g.cliente_id ? String(g.cliente_id) : "",
      fecha_gasto: g.fecha_gasto || hoyISO(),
      proveedor: g.proveedor || "",
      proveedor_nif: g.proveedor_nif || "",
      proveedor_direccion: g.proveedor_direccion || "",
      proveedor_poblacion: g.proveedor_poblacion || "",
      proveedor_provincia: g.proveedor_provincia || "",
      proveedor_codigo_postal: g.proveedor_codigo_postal || "",
      proveedor_telefono: g.proveedor_telefono || "",
      numero_documento: g.numero_documento || "",
      importe: importeParaInput(g.importe),
      moneda: g.moneda || "EUR",
      notas: g.notas || "",
      motivo_visita: g.motivo_visita || "",
      origen_direccion: g.origen_direccion || g.recorrido_km?.origen?.direccion || "",
      destino_direccion: g.destino_direccion || g.recorrido_km?.destino?.direccion || "",
      km: importeParaInput(g.km),
      tarifa_km: importeParaInput(g.tarifa_km) || String(tarifaKmDefault).replace(".", ","),
      visitas: g.visitas_km || [],
      ruta_comercial_id: g.ruta_comercial_id ? String(g.ruta_comercial_id) : "",
      km_ruta_estimada: g.km_ruta_estimada != null ? String(g.km_ruta_estimada) : "",
      recorrido: g.recorrido_km || null,
      mantener_orden_visitas: g.recorrido_km?.mantener_orden !== false,
      ivas: ivasFromApi(g.ivas),
      ruta_parada_id: g.ruta_parada_id || null,
      visita_label: g.cliente_nombre ? `Visita · ${g.cliente_nombre}` : (g.motivo_visita || ""),
    });
    if (g.cliente_id && g.cliente_nombre) {
      setClientes((prev) => (
        prev.some((c) => String(c.id) === String(g.cliente_id))
          ? prev
          : [{ id: g.cliente_id, nombre: g.cliente_nombre, activo: true }, ...prev]
      ));
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const enviar = async (forzar = false) => {
    if (necesitaTicket && !archivo && !editandoId) {
      toast.error("Sube una foto del ticket o factura");
      return;
    }
    if (esKm && (!form.km)) {
      toast.error("Indica los kilómetros recorridos");
      return;
    }
    setSubiendo(true);
    const fd = new FormData();
    appendFormData(fd, forzar);
    try {
      const req = editandoId
        ? api.put(`/gastos/${editandoId}`, fd, { headers: { "Content-Type": "multipart/form-data" } })
        : api.post("/gastos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await req;
      if (data?.geo_validacion?.alerta) {
        toast.warning(data.geo_validacion.mensaje || "Ubicación lejos de la visita");
      }
      toast.success(editandoId ? "Gasto actualizado" : "Gasto registrado correctamente");
      const volverRuta = !editandoId && form.ruta_parada_id;
      resetNuevo();
      cargar();
      try {
        const { data: sug } = await api.get("/gastos/sugerencias-visita");
        setFavoritas(sug.direcciones_favoritas || []);
      } catch { /* ignore */ }
      if (volverRuta) {
        toast.success("Volviendo a tu ruta de visitas");
        navigate("/mis-rutas");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.codigo === "gasto_duplicado") {
        if (window.confirm(`${detail.mensaje}\n¿Registrar de todas formas?`)) {
          await enviar(true);
          return;
        }
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setSubiendo(false);
    }
  };

  if (!cargandoEmpresa && empresa && !empresa.gestionar_gastos) {
    return <Navigate to="/fichar" replace />;
  }

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Receipt}
        title="Mis gastos"
        subtitle="Registra tickets, kilometraje, peajes, dietas y visitas con el mínimo tecleo"
        gradient="from-emerald-600 via-teal-600 to-cyan-600"
      />

      <section className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
        <h2 className="mb-3 text-sm font-semibold text-zinc-800">
          {editandoId ? "Modificar gasto pendiente" : "Nuevo gasto"}
        </h2>
        {editandoId && (
          <div className="mb-3 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <span>Estás editando un gasto pendiente. Puedes corregir datos o cambiar la foto.</span>
            <Button type="button" variant="ghost" size="sm" className="h-7 gap-1 text-amber-800" onClick={resetNuevo}>
              <X className="h-3.5 w-3.5" /> Cancelar
            </Button>
          </div>
        )}

        <div className="mb-4 flex flex-wrap gap-2">
          {(categorias.length ? categorias : [{ id: "ticket", label: "Ticket / factura" }]).map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setCategoria(c.id)}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                form.categoria === c.id
                  ? "border-emerald-600 bg-emerald-600 text-white"
                  : "border-zinc-200 bg-zinc-50 text-zinc-700 hover:border-emerald-300"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {esKm && (
            <>
              <KilometrajeForm
                form={form}
                setForm={setForm}
                archivoInicio={archivo}
                setArchivoInicio={setArchivo}
                previewInicio={preview}
                setPreviewInicio={setPreview}
                archivoFin={archivoFin}
                setArchivoFin={setArchivoFin}
                previewFin={previewFin}
                setPreviewFin={setPreviewFin}
                proyectos={proyectos}
                clientes={clientes}
                rutas={rutasHoy}
                usaProyectos={usaProyectos}
                usaRutas={usaRutas}
                tarifaKmDefault={tarifaKmDefault}
                onClienteCreado={(c) => setClientes((prev) => {
                  const ids = new Set(prev.map((x) => x.id));
                  if (ids.has(c.id)) {
                    return prev.map((x) => (x.id === c.id ? c : x));
                  }
                  return [...prev, c].sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
                })}
              />
              <Button className="w-full" disabled={subiendo || !formListo} onClick={() => enviar(false)}>
                {subiendo ? <Loader2 className="h-4 w-4 animate-spin" /> : (editandoId ? "Guardar cambios" : "Registrar kilometraje")}
              </Button>
            </>
          )}

          {necesitaTicket && (
            <div
              className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-6 ${
                preview ? "border-emerald-300 bg-emerald-50/30" : "border-zinc-200"
              }`}
            >
              {preview ? (
                <img src={preview} alt="Vista previa" className="mb-3 max-h-48 rounded-lg object-contain" />
              ) : editandoId ? (
                <GastoImagen
                  gastoId={editandoId}
                  className="mb-3 max-h-48 w-auto rounded-lg border object-contain"
                />
              ) : (
                <Receipt className="mb-2 h-10 w-10 text-zinc-300" />
              )}
              {escaneando && (
                <p className="mb-2 flex items-center gap-2 text-sm text-emerald-700">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Escaneando ticket…
                </p>
              )}
              <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf,.pdf"
                capture="environment"
                className="hidden"
                onChange={(e) => onArchivo(e.target.files?.[0])}
              />
              {!escaneado ? (
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" className="gap-1" disabled={escaneando} onClick={() => inputRef.current?.click()}>
                    <Upload className="h-4 w-4" /> Galería
                  </Button>
                  <Button type="button" variant="outline" size="sm" className="gap-1" disabled={escaneando} onClick={() => inputRef.current?.click()}>
                    <Camera className="h-4 w-4" /> Cámara
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => (editandoId ? inputRef.current?.click() : resetNuevo())}
                >
                  Cambiar imagen
                </Button>
              )}
              <p className="mt-2 text-center text-xs text-zinc-500">
                Foto el ticket: OCR rellena proveedor, importe, IVA y fecha
              </p>
            </div>
          )}

          {(escaneado && !esKm) && (
            <div className="space-y-3 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-emerald-800">
                {esKm ? <Car className="h-4 w-4" /> : <ScanLine className="h-4 w-4" />}
                {esKm ? "Desplazamiento — calcula km automáticamente" : "Datos detectados — puedes corregirlos"}
              </div>

              {(usaProyectos || form.ruta_parada_id) && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label>Proyecto / visita</Label>
                    <Select value={form.proyecto_id || "none"} onValueChange={(v) => setForm({ ...form, proyecto_id: v === "none" ? "" : v })}>
                      <SelectTrigger><SelectValue placeholder="Sin proyecto" /></SelectTrigger>
                      <SelectContent>
                        {form.ruta_parada_id && (
                          <SelectItem value="ruta">{form.visita_label || "Visita comercial"}</SelectItem>
                        )}
                        <SelectItem value="none">Sin proyecto</SelectItem>
                        {proyectos.map((a) => (
                          <SelectItem key={a.id} value={String(a.proyecto_id)}>
                            #{a.proyecto_codigo || a.proyecto_id} {a.proyecto_nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Cliente</Label>
                    <Select value={form.cliente_id || "none"} onValueChange={(v) => setForm({ ...form, cliente_id: v === "none" ? "" : v })}>
                      <SelectTrigger><SelectValue placeholder="Sin cliente" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin cliente</SelectItem>
                        {clientes.map((c) => (
                          <SelectItem key={c.id} value={String(c.id)}>
                            {c.codigo ? `${c.codigo} · ` : ""}{c.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <div>
                <Label>Motivo / visita</Label>
                <Input
                  value={form.motivo_visita}
                  onChange={(e) => setForm({ ...form, motivo_visita: e.target.value })}
                  placeholder="Ej. Visita comercial ACME, reunión de seguimiento…"
                />
              </div>

              {!esKm && (
                <>
                  <div>
                    <Label>Proveedor</Label>
                    <Input value={form.proveedor} onChange={(e) => setForm({ ...form, proveedor: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>NIF/CIF</Label>
                      <Input value={form.proveedor_nif} onChange={(e) => setForm({ ...form, proveedor_nif: e.target.value.toUpperCase() })} className="uppercase" />
                    </div>
                    <div>
                      <Label>Nº documento</Label>
                      <Input value={form.numero_documento} onChange={(e) => setForm({ ...form, numero_documento: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <Label>Dirección proveedor</Label>
                    <Input value={form.proveedor_direccion} onChange={(e) => setForm({ ...form, proveedor_direccion: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Población</Label>
                      <Input value={form.proveedor_poblacion} onChange={(e) => setForm({ ...form, proveedor_poblacion: e.target.value })} />
                    </div>
                    <div>
                      <Label>Provincia</Label>
                      <Input value={form.proveedor_provincia} onChange={(e) => setForm({ ...form, proveedor_provincia: e.target.value })} />
                    </div>
                  </div>
                </>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Fecha</Label>
                  <Input type="date" value={form.fecha_gasto} onChange={(e) => setForm({ ...form, fecha_gasto: e.target.value })} />
                </div>
                {!esKm && (
                  <div>
                    <Label>Importe ({form.moneda || "EUR"})</Label>
                    <Input type="text" inputMode="decimal" value={form.importe} onChange={(e) => setForm({ ...form, importe: e.target.value })} />
                  </div>
                )}
              </div>

              {!esKm && (
                <GastoIvasEditor
                  ivas={form.ivas}
                  onChange={(ivas) => setForm({ ...form, ivas })}
                  avisoSinDesglose={form.iva_incluido_sin_desglose}
                  avisoCalculado={form.ivas_calculados}
                />
              )}

              <div>
                <Label>Notas</Label>
                <Textarea value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} rows={2} />
              </div>

              {gps.latitud != null && (
                <p className="text-[11px] text-zinc-500">
                  Ubicación al registrar: {gps.latitud.toFixed(5)}, {gps.longitud.toFixed(5)}
                </p>
              )}

              <Button className="w-full" disabled={subiendo || !formListo} onClick={() => enviar(false)}>
                {subiendo ? <Loader2 className="h-4 w-4 animate-spin" /> : (editandoId ? "Guardar cambios" : "Registrar gasto")}
              </Button>
            </div>
          )}
        </div>
      </section>

      <section>
        <h2 className="mb-3 text-sm font-semibold text-zinc-800">Mis liquidaciones</h2>
        {liquidaciones.length === 0 ? (
          <p className="rounded-xl border border-dashed border-zinc-200 py-8 text-center text-sm text-zinc-500">
            Aún no tienes liquidaciones mensuales.
          </p>
        ) : (
          <ul className="space-y-2">
            {liquidaciones.map((l) => (
              <li
                key={l.id}
                className="flex items-center justify-between rounded-xl border border-zinc-200 bg-white px-4 py-3"
              >
                <div>
                  <p className="font-medium text-zinc-900">{l.periodo}</p>
                  <p className="text-xs text-zinc-500">{l.num_gastos} gastos · {l.total_km} km</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-emerald-700">{Number(l.total).toFixed(2)} €</p>
                  <Badge variant="outline" className="text-[10px]">{l.estado}</Badge>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="mb-3 text-sm font-semibold text-zinc-800">Historial</h2>
        {cargando ? (
          <div className="flex justify-center py-10"><Loader2 className="h-8 w-8 animate-spin text-emerald-500" /></div>
        ) : items.length === 0 ? (
          <p className="rounded-xl border border-dashed border-zinc-200 py-10 text-center text-sm text-zinc-500">
            Aún no has registrado gastos.
          </p>
        ) : (
          <ul className="space-y-3">
            {items.map((g) => {
              const st = ESTADO[g.estado] || ESTADO.pendiente;
              return (
                <li key={g.id} className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
                  <div className="flex gap-3 p-3">
                    <GastoImagen
                      gastoId={g.id}
                      tieneJustificante={g.tiene_justificante !== false}
                      className="h-20 w-20 rounded-lg border object-cover"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-zinc-900">{g.proveedor || g.categoria_label || "Gasto"}</p>
                        <Badge className="bg-zinc-100 text-[10px] text-zinc-700">{g.categoria_label || g.categoria}</Badge>
                        <Badge className={`text-[10px] ${st.cls}`}>{st.label}</Badge>
                        {g.estado === "aprobado" && g.liquidado_trabajador && (
                          <Badge className={`text-[10px] ${LIQUIDADO.cls}`}>{LIQUIDADO.label}</Badge>
                        )}
                      </div>
                      <p className="text-sm text-zinc-600">
                        {g.importe != null ? `${Number(g.importe).toFixed(2)} ${g.moneda || "€"}` : "—"}
                        {g.fecha_gasto && ` · ${fmtFecha(g.fecha_gasto)}`}
                        {g.km != null && ` · ${g.km} km`}
                        {g.km_ruta_estimada != null && ` · ruta ≈ ${g.km_ruta_estimada} km`}
                      </p>
                      {(g.origen_direccion || g.destino_direccion) && (
                        <p className="text-xs text-sky-700 line-clamp-1">
                          {g.origen_direccion} → {g.destino_direccion}
                        </p>
                      )}
                      {g.cliente_nombre && <p className="text-xs text-indigo-700">Cliente: {g.cliente_nombre}</p>}
                      {g.proyecto_nombre && (
                        <p className="text-xs text-violet-700">#{g.proyecto_codigo} {g.proyecto_nombre}</p>
                      )}
                      {g.geo_alerta && (
                        <p className="text-xs text-amber-700">
                          Ubicación lejos de la visita{g.distancia_visita_m != null ? ` (${Math.round(g.distancia_visita_m)} m)` : ""}
                        </p>
                      )}
                      {g.motivo_visita && <p className="text-xs text-zinc-500">{g.motivo_visita}</p>}
                      {g.estado === "aprobado" && g.liquidado_trabajador && (
                        <p className="text-xs text-sky-700">
                          Pagado / liquidado{g.nota_liquidacion ? `: ${g.nota_liquidacion}` : ""}
                        </p>
                      )}
                      {g.estado === "pendiente" && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="mt-2 h-8 gap-1"
                          onClick={() => editarPendiente(g)}
                        >
                          <Pencil className="h-3.5 w-3.5" /> Modificar
                        </Button>
                      )}
                      {g.categoria === "kilometraje" && g.estado !== "pendiente" && (
                        <p className="mt-1 text-[10px] text-zinc-400">Kilometraje ya revisado; no editable</p>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
